import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export interface UserInfo {
  email: string;
  id?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserInfo | null;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  setAuthenticated: (authenticated: boolean, user?: UserInfo | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  logout: () => void;
}

export type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,
  error: null,
};

/**
 * Vue Pinia authStore를 미러링하는 Zustand auth 스토어이다.
 *
 * Vue/React 하이브리드 단계에서는 Vue authStore가 기준 데이터 소스로 남는다.
 * VueAppBridge가 소유한 Pinia 인스턴스에서 syncVueAuthToReact로 동기화한다.
 */
export const useAuthStore = create<AuthStore>()(
  subscribeWithSelector((set) => ({
    ...initialState,

    setAuthenticated: (authenticated, user = null) =>
      set({
        isAuthenticated: authenticated,
        user: user ?? null,
        isLoading: false,
      }),

    setLoading: (loading) => set({ isLoading: loading }),

    setError: (error) => set({ error }),

    clearError: () => set({ error: null }),

    logout: () =>
      set({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: null,
      }),
  }))
);
